import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2, Mail, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_STORAGE_KEY = "pyg_access_token";

/**
 * /acceder — entrada al Centro de Evolución (vista clara).
 *
 * Dos flujos, una sola página:
 *   • Sin ?token=…   → solicitud (email → magic link enviado).
 *   • Con  ?token=… → verificación (validar → guardar → redirigir).
 *
 * UX: clara, premium, balanceada — coherente con /mi-evolucion.
 */
export default function AccederPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenParam = searchParams.get("token");
  const nextParam = searchParams.get("next") || "/mi-evolucion";

  // Request flow
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Verify flow
  const [verifying, setVerifying] = useState(Boolean(tokenParam));
  const [verifyError, setVerifyError] = useState(null);

  // Persistent session — if the user already has a valid token in
  // localStorage (from a previous magic-link / quiz auto-login on this
  // device), skip the email form and send them straight to /mi-evolucion.
  // Only runs when no ?token= is in the URL (verify flow takes priority).
  // Also skipped when ?expired=1 is present (we just logged them out).
  const [checkingSession, setCheckingSession] = useState(
    !tokenParam && searchParams.get("expired") !== "1"
  );

  useEffect(() => {
    if (tokenParam) return; // verify flow handles its own redirect
    if (searchParams.get("expired") === "1") {
      setCheckingSession(false);
      return;
    }
    let mounted = true;
    let stored = null;
    try { stored = localStorage.getItem(TOKEN_STORAGE_KEY); } catch { /* */ }
    if (!stored || stored.length < 16) {
      setCheckingSession(false);
      return;
    }
    (async () => {
      try {
        await axios.get(`${API}/me`, {
          headers: { "X-Access-Token": stored },
        });
        if (!mounted) return;
        // Token is still valid → straight to the dashboard.
        navigate(nextParam, { replace: true });
      } catch {
        // Token rejected → drop it and show the form.
        try {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(`${TOKEN_STORAGE_KEY}_meta`);
        } catch { /* */ }
        if (mounted) setCheckingSession(false);
      }
    })();
    return () => { mounted = false; };
  }, [tokenParam, nextParam, navigate, searchParams]);


  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/access/request-magic-link`, {
        email: cleaned,
        next: nextParam,
      });
    } catch {
      /* backend always returns 200; never block UX */
    } finally {
      setSent(true);
      setSubmitting(false);
    }
  }, [email, nextParam]);

  useEffect(() => {
    if (!tokenParam) return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/access/verify/${tokenParam}`);
        if (!mounted) return;
        if (data?.ok) {
          try {
            localStorage.setItem(TOKEN_STORAGE_KEY, tokenParam);
            localStorage.setItem(
              `${TOKEN_STORAGE_KEY}_meta`,
              JSON.stringify({
                lead_id: data.lead_id || null,
                user_id: data.user_id || null,
                purpose: data.purpose || null,
                verified_at: new Date().toISOString(),
              }),
            );
          } catch { /* localStorage disabled */ }
          setVerifying(false);
          setTimeout(() => navigate(nextParam, { replace: true }), 700);
        } else {
          setVerifyError("No pudimos validar tu acceso.");
          setVerifying(false);
        }
      } catch (err) {
        if (!mounted) return;
        const status = err?.response?.status;
        if (status === 410) {
          setVerifyError("Tu enlace expiró. Solicita uno nuevo abajo.");
        } else if (status === 404) {
          setVerifyError("Este enlace ya no es válido. Solicita uno nuevo.");
        } else {
          setVerifyError("No pudimos validar tu acceso. Intenta de nuevo.");
        }
        setVerifying(false);
      }
    })();
    return () => { mounted = false; };
  }, [tokenParam, nextParam, navigate]);

  return (
    <div
      data-testid="acceder-page"
      className="min-h-screen bg-[#F4F4F5] text-[#0A0A0A] relative overflow-hidden flex items-center justify-center px-6 py-16"
    >
      {/* Soft dot grid background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.55) 1px, transparent 0)",
          backgroundSize: "3px 3px",
        }}
      />
      {/* Soft teal halo */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[920px] h-[920px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(41,188,155,0.12) 0%, rgba(41,188,155,0) 60%)",
          filter: "blur(40px)",
        }}
      />

      <div className="relative w-full max-w-[520px]">
        {/* Brand mark */}
        <Link
          to="/"
          data-testid="brand-link"
          className="block text-center mb-12 text-sm tracking-[0.32em] font-semibold text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors"
        >
          PLAY YOUR GAME™
        </Link>

        {/* VERIFY FLOW */}
        {tokenParam && verifying && <VerifyingCard />}
        {tokenParam && !verifying && !verifyError && <SuccessCard />}
        {tokenParam && verifyError && <ErrorCard message={verifyError} />}

        {/* PERSISTENT SESSION CHECK — silent, brief */}
        {!tokenParam && checkingSession && <VerifyingCard />}

        {/* REQUEST FLOW */}
        {!tokenParam && !checkingSession && !sent && (
          <RequestCard
            email={email}
            setEmail={setEmail}
            submitting={submitting}
            onSubmit={handleSubmit}
          />
        )}
        {!tokenParam && !checkingSession && sent && <SentCard email={email} />}

        {/* Footer anchor */}
        <div className="mt-16 text-center text-[11px] tracking-[0.22em] uppercase text-zinc-400">
          La presión no cambia cómo juegas.{" "}
          <span className="text-[#29BC9B]">Revela cómo juegas.</span>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── Subcomponents ───────────────────────── //
function RequestCard({ email, setEmail, submitting, onSubmit }) {
  return (
    <form
      data-testid="acceder-request-form"
      onSubmit={onSubmit}
      className="space-y-8 animate-[fadeIn_700ms_ease-out]"
      style={{ animationFillMode: "both" }}
    >
      <div className="space-y-4">
        <div
          data-testid="acceder-eyebrow"
          className="text-[11px] tracking-[0.32em] uppercase text-[#29BC9B] font-semibold"
          style={{ animation: "fadeUp 700ms 80ms ease-out both" }}
        >
          Bienvenido de vuelta · Tu perfil
        </div>
        <h1
          data-testid="acceder-title"
          className="text-4xl sm:text-5xl font-semibold leading-[1.05] tracking-tight text-[#0A0A0A]"
          style={{ animation: "fadeUp 800ms 160ms ease-out both" }}
        >
          Ingresa a tu
          <br />
          perfil estratégico.
        </h1>
        <p
          className="text-[15px] text-zinc-500 leading-relaxed max-w-[440px]"
          style={{ animation: "fadeUp 800ms 280ms ease-out both" }}
        >
          Ingresa el mismo correo con el que hiciste tu test. Te enviaremos un
          enlace seguro para entrar a tu Dashboard, donde verás tu identidad
          estratégica, tu Mapa y tu Coach.
        </p>
      </div>

      <div
        className="space-y-3"
        style={{ animation: "fadeUp 800ms 380ms ease-out both" }}
      >
        <label
          htmlFor="acceder-email"
          className="block text-[11px] tracking-[0.22em] uppercase text-zinc-500 font-medium"
        >
          Tu correo estratégico
        </label>
        <div className="relative">
          <Mail
            className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400"
            size={16}
          />
          <input
            id="acceder-email"
            data-testid="acceder-email-input"
            type="email"
            required
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@correo.com"
            className="w-full bg-white border border-black/[0.10] rounded-sm px-4 py-4 pl-11 text-[15px] text-[#0A0A0A] placeholder-zinc-400 shadow-[0_1px_2px_rgba(0,0,0,0.02)] focus:outline-none focus:border-[#29BC9B] focus:bg-white focus:shadow-[0_0_0_4px_rgba(41,188,155,0.08)] transition-all duration-300"
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="acceder-submit-button"
        disabled={submitting || !email.trim()}
        className="group w-full bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold py-4 rounded-sm transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3"
        style={{ animation: "fadeUp 800ms 480ms ease-out both" }}
      >
        {submitting ? (
          <>
            <Loader2 className="animate-spin" size={16} />
            Enviando…
          </>
        ) : (
          <>
            Enviarme mi acceso
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform"
            />
          </>
        )}
      </button>

      <div
        className="flex items-start gap-2 text-[12px] text-zinc-500 leading-relaxed"
        style={{ animation: "fadeUp 800ms 600ms ease-out both" }}
      >
        <ShieldCheck size={14} className="text-[#29BC9B] mt-0.5 shrink-0" />
        <span>
          Enlace personal e intransferible. Vigencia de 30 días. No compartimos
          tu correo con nadie.
        </span>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </form>
  );
}

function SentCard({ email }) {
  return (
    <div
      data-testid="acceder-sent-state"
      className="space-y-6 animate-[fadeIn_700ms_ease-out]"
      style={{ animationFillMode: "both" }}
    >
      <div className="w-12 h-12 rounded-full border border-[#29BC9B]/40 bg-[#29BC9B]/10 flex items-center justify-center">
        <Mail className="text-[#29BC9B]" size={20} />
      </div>
      <div className="space-y-3">
        <div className="text-[11px] tracking-[0.32em] uppercase text-[#29BC9B] font-semibold">
          Enlace en camino
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold leading-[1.1] tracking-tight text-[#0A0A0A]">
          Revisa tu correo.
        </h1>
        <p className="text-[15px] text-zinc-600 leading-relaxed max-w-[440px]">
          Si <span className="text-[#0A0A0A] font-medium">{email || "ese correo"}</span> está
          registrado en Play Your Game™, recibirás en breve un enlace para
          entrar a tu Centro de Evolución.
        </p>
        <p className="text-[13px] text-zinc-500 leading-relaxed pt-2">
          ¿No lo encuentras? Revisa promociones o spam. El enlace puede tardar
          hasta un minuto.
        </p>
      </div>
    </div>
  );
}

function VerifyingCard() {
  return (
    <div data-testid="acceder-verifying-state" className="space-y-6 text-center">
      <Loader2 className="mx-auto text-[#29BC9B] animate-spin" size={28} />
      <div className="space-y-2">
        <div className="text-[11px] tracking-[0.32em] uppercase text-[#29BC9B] font-semibold">
          Validando tu acceso
        </div>
        <p className="text-[15px] text-zinc-500">
          Estamos abriendo la puerta a tu evolución estratégica…
        </p>
      </div>
    </div>
  );
}

function SuccessCard() {
  return (
    <div
      data-testid="acceder-success-state"
      className="space-y-6 text-center animate-[fadeIn_500ms_ease-out]"
      style={{ animationFillMode: "both" }}
    >
      <div className="mx-auto w-12 h-12 rounded-full border border-[#29BC9B]/50 bg-[#29BC9B]/15 flex items-center justify-center">
        <ShieldCheck className="text-[#29BC9B]" size={22} />
      </div>
      <div className="space-y-2">
        <div className="text-[11px] tracking-[0.32em] uppercase text-[#29BC9B] font-semibold">
          Acceso verificado
        </div>
        <p className="text-[15px] text-zinc-600">
          Llevándote a tu Centro de Evolución…
        </p>
      </div>
    </div>
  );
}

function ErrorCard({ message }) {
  return (
    <div
      data-testid="acceder-error-state"
      className="space-y-6 animate-[fadeIn_500ms_ease-out]"
      style={{ animationFillMode: "both" }}
    >
      <div className="text-[11px] tracking-[0.32em] uppercase text-red-500 font-semibold">
        Enlace no válido
      </div>
      <h1 className="text-3xl sm:text-4xl font-semibold leading-[1.1] tracking-tight text-[#0A0A0A]">
        {message}
      </h1>
      <p className="text-[15px] text-zinc-600 leading-relaxed max-w-[440px]">
        Ingresa tu correo abajo y te enviaremos un enlace nuevo, válido por 30 días.
      </p>
      <Link
        to="/acceder"
        data-testid="acceder-retry-link"
        className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-bold text-[#29BC9B] hover:text-[#22a989] transition-colors"
      >
        Solicitar nuevo enlace
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}
